import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faShield, faInfo } from "@fortawesome/free-solid-svg-icons";
import Loader from "../../components/Loader";
import LoaderBilan from "../LoaderBilan";
import { getDashboardNetworks, getDashboardDevices, getIotDevices } from "../../scripts/api_dashboard";
import SimpleDoughnutChart from "../SimpleGraphDoughnut";
import { useTheme } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { BsFillWebcamFill } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import SimpleGraphLine from "../SimpleGraphLine";
import StatisticsCard from "../Card/StatisticsCard";

/**
 * Composant pour afficher le nombres d'équipements...
 * @param {string} title - titre de la carte
 * @param {number} number - longueur du tableau à afficher
 * @param {string} logo - URL si vous souhaitez afficher une image
 * @param {string} color - couleur du logo
 * @param {string} stylesIcons - arrière-plan du logo
 */
export default function Card({ lien, logo, title, infos, data, graphDough, graphLine, colors, type, bilanData, loadingBilan }) {
    const { apiUrl, entrepriseRoles } = useContext(AuthContext);
    const queryClient = useQueryClient();
    const { theme } = useTheme();
    const [merakiData, setMerakiData] = useState(false);
    const [webexDataDashboard, setWebexDataDashboard] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const hasMerakiRole = entrepriseRoles?.includes("ROLE_MERAKI");
    const hasWebexRole = entrepriseRoles?.includes("ROLE_WEBEX");
    const hasIotRole = entrepriseRoles?.includes("ROLE_WEBEX") || entrepriseRoles?.includes("ROLE_MERAKI");
    const isBilanServiceInactive = bilanData === "service_no_active";

    const dashboardNetworks = useQuery({
        queryKey: ["dashboardNetworks"],
        queryFn: () => getDashboardNetworks(apiUrl),
        enabled: hasMerakiRole
    });

    const dashboardDevices = useQuery({
        queryKey: ["dashboardDevices"],
        queryFn: () => getDashboardDevices(apiUrl),
        enabled: hasWebexRole
    });
    const dashboardIotDevices = useQuery({
        queryKey: ["dashboardIotDevices"],
        queryFn: () => getIotDevices(apiUrl),
        enabled: hasIotRole
    });

    useEffect(() => {
        if (dashboardNetworks.isFetched) {
            if (dashboardNetworks.data) {

                const online = dashboardNetworks.data.filter(
                    device => device.status === "online"
                ).length;

                const offline = dashboardNetworks.data.filter(
                    device => device.status !== "online"
                ).length;

                let arrayMerakiData = {
                    [t("Connectés")]: online,
                    [t("Inactifs")]: offline,
                };

                setMerakiData(arrayMerakiData);
            } else {

                setMerakiData({
                    [t("Connectés")]: 0,
                    "Hors-ligne": 0,
                });
            }
        }
    }, [dashboardNetworks.data, dashboardNetworks.isFetched]);

    useEffect(() => {
        if (!dashboardDevices.isLoading && dashboardDevices.isFetched) {
            if (dashboardDevices.data && Array.isArray(dashboardDevices.data.items)) {
                // Compter le nombre d'appareils "Hors-ligne expiré"
                const offlineExpiredCount = dashboardDevices.data.items.filter(
                    device => device.connectionStatus === "offline_expired"
                ).length;

                // Compter le nombre d'appareils "Connecté"
                const connectedCount = dashboardDevices.data.items.filter(
                    device => device.connectionStatus === "connected"
                ).length;

                // Créer un objet avec les comptes
                const deviceStatusCounts = {
                    [t("Connectés")]: connectedCount,
                    [t("Hors-ligne expirés")]: offlineExpiredCount
                };
                setWebexDataDashboard(deviceStatusCounts);
            } else {
                setWebexDataDashboard({
                    [t("Connectés")]: 0,
                    [t("Hors-ligne expirés")]: 0
                });
            }
        }
    }, [dashboardDevices.data, dashboardDevices.isFetched]);

    if (data === "Loading") {
        return (
            <div className="card-components">
                <Loader />
            </div>
        )
    }

    if (data && data[0] && data[0].text === "Data not found") {
        return (
            <div className="card-components card-components-disabled">
                <div className="card-components-header">
                    {logo && (
                        <img className="card-components-header-logo-image" src={logo} alt={`Logo ${title}`} />
                    )}
                    <h2 className="card-components-header-title">
                        {t(title)}
                    </h2>
                    {infos && (
                        <span className="card-components-header-info tooltip-container">
                            <FontAwesomeIcon icon={faInfo} />
                            <span className="tooltip-text">{infos}</span>
                        </span>
                    )}
                </div>
                <div className="card-components-disabled-content">
                    <h2>{t("Service non actif")}</h2>
                </div>
            </div>
        )
    }

    if (type === "bilan") {
        return (
            <div className={`card-components`}>
                <div className="card-components-header">
                    <h2 className="card-components-header-title card-components-header-title-bilan">
                        {t("BILAN GLOBAL")}
                    </h2>
                    {infos && (
                        <span className="card-components-header-info tooltip-container">
                            <FontAwesomeIcon icon={faInfo} />
                            <span className="tooltip-text">{infos}</span>
                        </span>
                    )}
                </div>
                <div className="card-content-bilan">
                    <div className="card-content-bilan-container">
                        <div className={`card-content-bilan-logo card-logo-visio`}>
                            <BsFillWebcamFill color={"#997ADB"} size={30} />
                        </div>
                        <div className="card-content-bilan-text">
                            <div>{t("Equipements")} Visio</div>
                            <div>
                                {loadingBilan ? (
                                    <LoaderBilan />
                                ) : isBilanServiceInactive ? (
                                    <div className="card-content-bilan-text-disabled-content">
                                        {t("Service non actif")}
                                    </div>
                                ) : (
                                    bilanData.visio && bilanData.visio.length || 0
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card-content-bilan-container">
                        <div className={`card-content-bilan-logo card-logo-certificats`}>
                            <FontAwesomeIcon icon={faCircleCheck} color={"#AEC353"} />
                        </div>
                        <div className="card-content-bilan-text">
                            <div>{t("Certificats")}</div>
                            <div>
                                {loadingBilan ? (
                                    <LoaderBilan />
                                ) : isBilanServiceInactive ? (
                                    <div className="card-content-bilan-text-disabled-content">
                                        {t("Service non actif")}
                                    </div>
                                ) : (
                                    bilanData.certificats && bilanData.certificats.length || 0
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card-content-bilan-container">
                        <img className="card-components-header-logo-image-bilan" src="../assets/cisco_logo.png" alt={`Logo Cisco`} />
                        <div className="card-content-bilan-text">
                            <div>{t("Contrats")} Cisco</div>
                            <div>
                                {loadingBilan ? (
                                    <LoaderBilan />
                                ) : isBilanServiceInactive ? (
                                    <div className="card-content-bilan-text-disabled-content">
                                        {t("Service non actif")}
                                    </div>
                                ) : (
                                    bilanData.contractsCisco && bilanData.contractsCisco.length || 0
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card-content-bilan-container">
                        <div className={`card-content-bilan-logo card-logo-firewall`}>
                            <FontAwesomeIcon icon={faShield} color={"#FA8334"} />
                        </div>
                        <div className="card-content-bilan-text">
                            <div>{t("Licences")} Sécurité</div>
                            <div>
                                {loadingBilan ? (
                                    <LoaderBilan />
                                ) : isBilanServiceInactive ? (
                                    <div className="card-content-bilan-text-disabled-content">
                                        {t("Service non actif")}
                                    </div>
                                ) : (
                                    bilanData.licencesFirewall && bilanData.licencesFirewall.length || 0
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card-content-bilan-container">
                        <img className="card-components-header-logo-image-bilan" src="../assets/cisco_logo.png" alt={`Logo Cisco`} />
                        <div className="card-content-bilan-text">
                            <div>{t("Subscription")}</div>
                            <div>
                                {loadingBilan ? (
                                    <LoaderBilan />
                                ) : isBilanServiceInactive ? (
                                    <div className="card-content-bilan-text-disabled-content">
                                        {t("Service non actif")}
                                    </div>
                                ) : (
                                    bilanData.subscription && bilanData.subscription.length || 0
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <section className="card-components-content-graph-bilan">
                    <StatisticsCard
                        title="Meraki"
                        logo="../assets/cisco_logo_grey.png"
                        text={t("Nombre d’équipements connectés et inactifs")}
                        data={!hasMerakiRole ? false : dashboardNetworks.isLoading ? undefined : merakiData}
                    />
                    <StatisticsCard
                        title="Webex"
                        logo="../assets/webex_logo.png"
                        text={t("Nombre d'appareils Webex Connectés ou Hors-ligne expirés")}
                        data={!hasWebexRole ? false : dashboardDevices.isLoading ? undefined : webexDataDashboard}
                    />
                    <StatisticsCard
                        title="Iot"
                        text={t("Nombre d'appareils Meraki et Webex")}
                        data={!hasIotRole ? false : dashboardIotDevices.isLoading ? undefined : dashboardIotDevices.data}
                    />
                </section>
            </div>
        )
    }

    return (
        <div className={`card-components`} onClick={() => { navigate(lien) }}>
            <>
                <div className="card-components-header">
                    {logo && (
                        <img className="card-components-header-logo-image" src={logo} alt={`Logo ${title}`} />
                    )}
                    <h2 className="card-components-header-title">
                        {t(title)}
                    </h2>
                    {infos && (
                        <span className="card-components-header-info tooltip-container">
                            <FontAwesomeIcon icon={faInfo} />
                            <span className="tooltip-text">{infos}</span>
                        </span>
                    )}
                </div>
                {graphDough ? (
                    <section className={title === 'Secure Endpoint' ? "card-components-content" : "sentinel-one"}>
                        <SimpleDoughnutChart key={theme} data={data} colors={colors} title={title} legend={true} />
                    </section>
                ) : graphLine ? (
                    <SimpleGraphLine key={theme} data={data} />
                ) : (
                    data && data.length > 0 && (
                        <>
                            {data.map((item, index) => (
                                <div key={index} className="card-components-item">
                                    <p>{t(item.text)} : <span className={`card-components-item-card-${item.color}`}>{item.number}</span></p>
                                </div>
                            ))}
                        </>
                    )
                )}
            </>
        </div>
    );
}
